import { useCallback, useEffect, useState } from 'react';

// Detects the user's current city for the navbar pill. Attempts browser
// geolocation → reverse-geocodes via BigDataCloud's keyless client endpoint
// (10k/day free, no API key). Caches the result in localStorage so we don't
// re-prompt on every page load; silently falls back to the default city if
// permission is denied, GPS fails, or the network call errors.
//
// Exposes:
//   city       — string to render in the pill ('Hyderabad' while detecting)
//   status     — 'idle' | 'detecting' | 'ok' | 'error'
//   refresh()  — force re-detect (ignores cache, re-prompts if needed)

const DEFAULT_CITY = 'Hyderabad';
const CACHE_KEY = 'trustlab_detected_city';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.city || !parsed?.at) return null;
    if (Date.now() - parsed.at > CACHE_TTL_MS) return null;
    return parsed.city;
  } catch {
    return null;
  }
};

const writeCache = (city) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ city, at: Date.now() }));
  } catch {
    /* storage full / disabled — ignore */
  }
};

const reverseGeocode = async (lat, lng) => {
  const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`reverse-geocode ${res.status}`);
  const data = await res.json();
  // Prefer `city`; fall back to `locality` → principalSubdivision → country.
  return (
    data.city ||
    data.locality ||
    data.principalSubdivision ||
    data.countryName ||
    null
  );
};

const useDetectedCity = () => {
  const [city, setCity] = useState(() => readCache() || DEFAULT_CITY);
  const [status, setStatus] = useState(() => (readCache() ? 'ok' : 'idle'));

  const detect = useCallback((force = false) => {
    if (!force && readCache()) return;
    if (!('geolocation' in navigator)) {
      setStatus('error');
      return;
    }
    setStatus('detecting');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const resolved = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
          if (resolved) {
            setCity(resolved);
            writeCache(resolved);
            setStatus('ok');
          } else {
            setStatus('error');
          }
        } catch {
          setStatus('error');
        }
      },
      () => setStatus('error'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60 * 60 * 1000 }
    );
  }, []);

  useEffect(() => {
    detect(false);
  }, [detect]);

  const refresh = useCallback(() => detect(true), [detect]);

  return { city, status, refresh };
};

export default useDetectedCity;
